import { useState } from 'react';
import { Button, Input, Space, Table, message } from 'antd';
import { DownloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { personsApi, type PersonListItem } from '../../../api/endpoints/persons';
import { downloadBlob } from '../../../api/downloadBlob';
import { PageHeader } from '../../../components/common/PageHeader';

export function PersonsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isFetching } = useQuery({
    queryKey: ['persons', { page, pageSize, search }],
    queryFn: () =>
      personsApi.list({ page, pageSize, search: search || undefined, sortDir: 'asc' }),
  });

  const handleExport = (): void => {
    downloadBlob('/persons/export.csv', 'contactos.csv', {
      search: search || undefined,
    }).catch(() => message.error('No se pudo exportar'));
  };

  const columns: ColumnsType<PersonListItem> = [
    {
      title: 'Nombre',
      render: (_, row) => (
        <Link to={`/contactos/${row.id}`}>
          {row.firstName ?? ''} {row.lastName}
        </Link>
      ),
    },
    { title: 'Puesto', dataIndex: 'jobTitle' },
    {
      title: 'Cuenta',
      render: (_, row) =>
        row.account ? (
          <Link to={`/cuentas/${row.account.id}`}>{row.account.name}</Link>
        ) : (
          '—'
        ),
    },
    { title: 'Correo', dataIndex: 'email' },
    { title: 'Teléfono', dataIndex: 'phone' },
    { title: 'Móvil', dataIndex: 'mobile' },
  ];

  return (
    <>
      <PageHeader
        title="Contactos"
        crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Contactos' }]}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Exportar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/contactos/nuevo')}
            >
              Nuevo contacto
            </Button>
          </Space>
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nombre, correo, teléfono…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
      </Space>

      <Table<PersonListItem>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} contactos`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </>
  );
}
